/*
 *   File : ops-dashboard.js
 *   Author : https://evoqins.com
 *   Description : Ops dashboard file
 *   Integrations : NA
 *   Version : 1.0.0
*/

import { Fragment, useEffect, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';

// custom style
import color from '../../Style/color.scss';

// custom components
import { Chip, CustomTab, EmptyScreen, Loader } from '../../Components/Others';
import { CustomSelectBox, DateRangePicker } from '../../Components/FormElements';
import { Icon } from '../../Components/Icon';
import { PrimaryButton, TextButton } from '../../Components/Buttons';
import { CustomDataTable } from '../../Components/DataTable';
import { APIService } from '../../Service';
import { format } from 'date-fns';
import { ViewApisModal } from '../../Components/Modal';
import { Modal } from 'bootstrap';

const TAB_TITLES = [
    { id: 1, label: 'Cron Status' },
    { id: 2, label: 'API Status' }
];

const OPTIONS = [
    {
        value: 1,
        label: 'Weekly',
    },
    {
        value: 2,
        label: 'Monthly',
    },
    {
        value: 3,
        label: 'Yearly',
    }
];

const ITEMS_PER_PAGE = 20;

const OPS_VIEW_APIS_SESSION_KEY = 'ops_view_apis_restore';

const OpsDashboard = () => {

    const { state } = useLocation();
    const navigate = useNavigate();
    const scrollRef = useRef(null);

    const isRendered = useRef(true);

    const [selectedTab, setSelectedTab] = useState(0);
    const [selectedRegistration, setSelectedRegistration] = useState(OPTIONS[1]);

    const [filterToggle, setFilterToggle] = useState(false);

    const [currentPage, setCurrentPage] = useState(1);

    const [filterData, setFilterData] = useState([]);
    const [filterLoader, setFilterLoader] = useState(true);

    const [selectedFilter, setSelectedFilter] = useState(null);
    const [selectedDate, setSelectedDate] = useState(null);

    const [loader, setLoader] = useState(true);
    const [listData, setListData] = useState({});

    const [isRender, srtIsRender] = useState(true);

    const [viewApisModal, setViewApisModal] = useState(false);
    const [viewApisData, setViewApisData] = useState([]);


    useEffect(() => {
        window.scrollTo({
            top: 0, behavior: 'smooth'
        });
    }, []);

    useEffect(() => {
        // Explicit close from View APIs modal
        if (Array.isArray(state?.api_data) && state.api_data.length === 0) {
            setViewApisData([]);
            setViewApisModal(false);
            try {
                sessionStorage.removeItem(OPS_VIEW_APIS_SESSION_KEY);
            } catch (_) {
                /* ignore */
            }
            return;
        }

        let apiPayload = null;

        // Breadcrumb / navigate back from api-history with restore payload
        if (state?.api_data && !Array.isArray(state.api_data) && state.api_data?.apis) {
            apiPayload = state.api_data;
            try {
                sessionStorage.removeItem(OPS_VIEW_APIS_SESSION_KEY);
            } catch (_) {
                /* ignore */
            }
        } else {
            // One-shot browser-back restore only — consumed so later OPS visits
            // (e.g. after Customer) do not reopen the modal.
            try {
                const raw = sessionStorage.getItem(OPS_VIEW_APIS_SESSION_KEY);
                if (raw) {
                    const parsed = JSON.parse(raw);
                    sessionStorage.removeItem(OPS_VIEW_APIS_SESSION_KEY);
                    if (parsed?.api_data) {
                        apiPayload = Array.isArray(parsed.api_data)
                            ? { name: parsed.name || '', apis: parsed.api_data }
                            : parsed.api_data;
                    }
                }
            } catch (_) {
                /* ignore */
            }
        }

        if (apiPayload?.apis?.length > 0) {
            setViewApisData(apiPayload);
            setViewApisModal(true);
        }
    }, [state]);

    useEffect(() => {
        if (state !== null) {
            if (state?.tab_index) {
                setSelectedTab(state.tab_index);
                // srtIsRender(true);
                _handleClear();
            } else {
                setSelectedTab(1);
            }
        }
        else {
            setSelectedTab(1);
        }
    }, [state]);

    useEffect(() => {
        if (isRendered.current === true) {
            _getFilterData();
            isRendered.current = false;
        }
    }, [isRendered.current]);

    useEffect(() => {
        if (selectedTab !== 0 && isRender === true) {
            _getOpsData(1);
            // setLoader(true);
            srtIsRender(false);
        }
    }, [selectedTab, isRender]);

    useEffect(() => {
        if (viewApisModal === true) {
            const modal = new Modal(document.getElementById("view-apis-modal"));
            modal.show();
        }
    }, [viewApisModal]);


    // handle tab
    const _handleTab = (tab_id) => {
        setLoader(true);
        navigate('/ops', {
            state: {
                tab_index: tab_id
            }
        });

    };

    // handle filter
    const _handleFilterToggle = () => {
        setFilterToggle(prev => !prev);
    };

    // handle clear filter
    const _handleClear = () => {
        setSelectedFilter(null);
        setSelectedDate(null);

        srtIsRender(true);
    };

    // handle selected filter
    const _handleFilter = () => {
        setCurrentPage(1);
        _getOpsData(1);
        setLoader(true);
    };


    // handle table page change 
    const _handlePageChange = (page_num) => {
        setCurrentPage(page_num);
        if (scrollRef.current) {
            scrollRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' })
        }

        _getOpsData(page_num);
        setLoader(true);
    };

    // Table column
    const th = (label) => <span className="e-table-th-wrap">{label}</span>;
    const thLines = (...lines) => (
        <span className="e-table-th-wrap">
            {lines.map((line, i) => (
                <Fragment key={i}>
                    {i > 0 ? <br /> : null}
                    {line}
                </Fragment>
            ))}
        </span>
    );

    const TABLE_COLUMN = [
        {
            name: th('Cron Name'),
            cell: row => <>
                {row.name}
            </>,
            width: "340px",
            grow: 1,
            id: "ops_cron_name",
        },
        {
            name: thLines('Success/', 'Failed'),
            cell: row => <Chip label={row.cron_status}
                className={row.cron_status === "Success" ? "e-bg-green-crayola" : "e-bg-maximum-red"} />,
            width: "140px",
            grow: 0,
            id: "ops_cron_status",
        },
        {
            name: th('Timestamp'),
            selector: row => row.updated_at || "-",
            width: "180px",
            grow: 0,
            id: "ops_cron_timestamp",
        },
        {
            name: th('Action'),
            selector: row => "-",
            cell: row => <span className='e-text-blue-violet text-truncate text-decoration-hover-underline cursor-pointer e-font-weight-500 '
                onClick={() => _handleDetail(row.id)}>
                View history
            </span>,
            width: "120px",
            grow: 0,
            id: "ops_cron_action",
        },
    ];


    const _expandableRows = ({ data }) => {

        const bgClass = data._index % 2 === 0 ? 'e-bg-magnolia-dark' : 'e-bg-magnolia-extra-dark';

        if (!data.sub_groups || data.sub_groups.length === 0) {
            return <div className={`${bgClass} p-3 e-text-black e-primary-font e-font-14 e-font-weight-400 line-height-16px`}>No schemes available.</div>;
        }

        return (
            <div className={`py-3 padding-30px-lr d-flex flex-column gap-12px ${bgClass} `} >
                {data.sub_groups.map((child_data, index) => (
                    <div key={index} className='d-flex align-items-center '>
                        <p className='mb-0 e-text-smoky-black e-alt-font e-font-14 e-font-weight-400 line-height-24px' style={{ width: "520px" }}>
                            {child_data.name || "-"}
                        </p>

                        {/* <div style={{ width: "180px" }}>
                            {child_data.api_status !== null ?
                                <Chip label={child_data.api_status}
                                    textAlign="justify-content-start"
                                    className={`${child_data.api_status?.toLowerCase() === "success" ? "e-text-green-crayola e-border-1px-green-crayola" : "e-text-maximum-red e-border-1px-maximum-red"} bg-transparent `} />
                                :
                                "-"
                            }
                        </div>

                        <p className='mb-0 e-text-smoky-black e-alt-font e-font-14 e-font-weight-400 line-height-24px' style={{ width: "180px" }}>
                            {child_data.created_at_date || "-"}
                        </p> */}

                        <div style={{ width: "120px" }}>
                            <TextButton name="View APIs"
                                className='e-text-blue-violet text-truncate text-decoration-hover-underline cursor-pointer '
                                onClick={() => {
                                    setViewApisModal(true);
                                    if (child_data.apis) {
                                        setViewApisData(child_data);
                                    } else {
                                        setViewApisData([]);
                                    }
                                }}
                            />
                        </div>
                    </div>
                ))}
            </div>
        );
    };

    const TABLE_COLUMN_API_STATUS = [
        {
            name: th('API Name'),
            cell: row => <>
                {row.name}
            </>,
            width: "520px",
            grow: 1,
            id: "ops_api_name",
        },
        // {
        //     name: 'Success/ Failed',
        //     cell: row =>
        //         row.api_status !== null ? <Chip label={row.api_status}
        //             className={row.api_status?.toLowerCase() === "success" ? "e-bg-green-crayola" : "e-bg-maximum-red "} /> : "-",
        //     width: "180px",
        // },
        // {
        //     name: 'Timestamp',
        //     selector: row => row.created_at_date || "-",
        //     width: "180px"
        // },
        {
            name: th('Action'),
            selector: row => "",
            // cell: row => <span className='e-text-blue-violet text-truncate text-decoration-hover-underline cursor-pointer '
            //     onClick={() => {
            //         navigate('api-history', {
            //             state: {
            //                 id: row.id,
            //                 tab_index: selectedTab
            //             }
            //         })
            //     }}>
            //     View history
            // </span>,
            width: "120px",
            grow: 0,
            id: "ops_api_action",
        },
    ];

    const _handleDetail = (id) => {
        navigate('cron-history', {
            state: {
                id: id,
                tab_index: selectedTab
            }
        })
    }


    // API - Get ops data
    const _getOpsData = (page_num = currentPage) => {

        const start_date = selectedDate === null ? null : format(new Date(selectedDate[0]), 'dd-MM-yyyy');
        const end_date = selectedDate === null ? null : format(new Date(selectedDate[1]), 'dd-MM-yyyy');

        let is_post = true;
        let url = 'ops-dashboard/get';
        let data = {
            "page_num": page_num,
            "page_size": ITEMS_PER_PAGE,
            "filter_by": selectedFilter !== null ? selectedFilter.value : null,
            "start_date": start_date,//dd- mm - yyyy
            "end_date": end_date,
            "type": selectedTab
        };
        APIService(is_post, url, data).then((response) => {
            if (response.status_code === 200) {

                if (selectedTab === 2) {
                    const updated_data = response.data.data.map((item, index) => ({
                        ...item,
                        _index: index
                    }));
                    setListData({
                        ...response.data,
                        data: updated_data
                    });
                } else {
                    setListData(response.data);
                }
            }
            else {
                setListData({});
            }
            setLoader(false);
            srtIsRender(false);
        });
    };

    // API - Get filter data
    const _getFilterData = () => {
        let is_post = false;
        let url = 'ops-dashboard/filter-data';
        let data = {};
        APIService(is_post, url, data).then((response) => {
            if (response.status_code === 200) {

                // let updated_data = [
                //     { value: null, label: "All" },
                //     ...response.data.flat()
                // ];


                setFilterData(response.data);
            } else {
                setFilterData([]);
            }
            setFilterLoader(false);
        });
    };



    const FAILURE_TABLE_COLUMN = [
        {
            name: th('Name'),
            cell: row => <>
                {row.name}
            </>,
            width: "300px",
            grow: 1,
            id: "ops_fail_name",
        },
        {
            name: thLines('Success/', 'Failed'),
            cell: row =>
                row.api_status !== null ? <Chip label={row.api_status}
                    className={row.api_status?.toLowerCase() === "success" ? "e-bg-green-crayola" : "e-bg-maximum-red "} /> : "-",
            width: "170px",
            grow: 0,
            id: "ops_fail_status",
        },
        {
            name: th('Failure Type'),
            selector: row => row.failure_type || "-",
            width: "220px",
            grow: 0,
            id: "ops_fail_type",
        },
        {
            name: th('Timestamp'),
            selector: row => row.created_at || "-",
            width: "190px",
            grow: 0,
            id: "ops_fail_timestamp",
        },
        {
            name: th('Action'),
            selector: row => "-",
            cell: row => <span className='e-text-blue-violet text-truncate text-decoration-hover-underline cursor-pointer e-font-weight-500 '
                onClick={() => {
                    navigate('/ops/api-history', {
                        state: {
                            id: row.id,
                            tab_index: 2,
                        }
                    })
                }}>
                View history
            </span>,
            width: "120px",
            grow: 0,
            id: "ops_fail_action",
        },
    ];

    return (
        <Fragment>
            <section className='e-section-height pb-5' >
                <div className='e-bg-lavender padding-52px-lr pt-2 padding-112px-bottom'>
                    <p className='mb-0 e-text-chinese-black e-primary-font e-font-24 e-font-weight-600 line-height-24px'>
                        OPS Dashboard
                    </p>
                </div>
                <div className='padding-52px-lr margin--80px-top'>
                    {/* Order summary */}
                    <div ref={scrollRef} className='e-bg-ghost-white border-radius-16 e-box-shadow-ops-wrapper '>
                        <div className=' padding-32px-tb'>
                            <div className='padding-32px-lr d-flex align-items-center justify-content-between pb-3 gap-16px'>
                                <CustomTab data={TAB_TITLES}
                                    selectedTab={selectedTab}
                                    handleClick={_handleTab} />

                                <div className={` padding-10px-all border-radius-8 e-cursor-pointer e-border-1px-light-purple cursor-pointer   ${filterToggle ? ' e-bg-blue-violet ' : ''}`}
                                    onClick={_handleFilterToggle}>
                                    <Icon icon='filter'
                                        size='1.25rem'
                                        className='d-flex'
                                        color={filterToggle && color.white} />
                                </div>
                            </div>

                            <div className={`e-filter-content  ${filterToggle ? 'expanded ' : 'e-overflow collapsed'}`}>
                                <div className='e-bg-main-white pt-3 pb-4 padding-32px-lr'>
                                    <div className='d-flex justify-content-end'>
                                        <TextButton name="Clear all"
                                            onClick={_handleClear}
                                        />
                                    </div>
                                    <div className='row padding-32px-bottom'>
                                        <div className='col-3'>
                                            <CustomSelectBox
                                                title={"Filter by"}
                                                placeholder='Select'
                                                controlWidth='100%'
                                                loader={filterLoader}
                                                options={filterData}
                                                isSearchable={false}
                                                value={selectedFilter}
                                                onChange={(value) => {
                                                    setSelectedFilter(value);
                                                    setSelectedDate(null);
                                                }} />
                                        </div>


                                        <div className='col-3'>
                                            {selectedFilter !== null && selectedFilter.value === null &&
                                                <DateRangePicker label="Custom date"
                                                    placeholder="Select"
                                                    width="w-100"
                                                    maxDate={new Date}
                                                    value={selectedDate}
                                                    handleClear={() => setSelectedDate(null)}
                                                    handleChange={(date) => setSelectedDate(date)} />
                                            }
                                        </div>
                                    </div>

                                    <PrimaryButton name='Filter now'
                                        paddingBlock='padding-12px-tb padding-40px-lr'
                                        onClick={_handleFilter} />
                                </div>
                            </div>


                            {loader === true ?
                                <Loader className="py-5 min-vh-100" />
                                :
                                Object.keys(listData).length !== 0 && listData?.data?.length !== 0 ?
                                    <>
                                        {selectedTab === 1 ?
                                            <div className='e-data-table-wrapper-default-updated pt-3'>
                                                <CustomDataTable columns={TABLE_COLUMN}
                                                    data={listData.data}
                                                    pagination={true}
                                                    paginationPerPage={ITEMS_PER_PAGE}
                                                    paginationDefaultPage={listData.current_page}
                                                    paginationTotalRows={listData.total_count}
                                                    lastPage={listData.total_page}
                                                    onChangePage={_handlePageChange}
                                                />
                                            </div>
                                            :

                                            <>
                                                <h6 className='px-3 e-text-chinese-black e-primary-font e-font-16 e-font-weight-600 line-height-24px'>
                                                    API Failure Logs
                                                </h6>
                                                {listData.failure_data && listData.failure_data.length > 0 ?
                                                    <div className='e-border-1px-platinum-light  border-radius-8 pt-1 mx-3'>
                                                        <div className={`e-data-table-wrapper-default-updated`}>
                                                            <CustomDataTable columns={FAILURE_TABLE_COLUMN}
                                                                data={listData.failure_data}
                                                                pagination={false}
                                                                paginationDefaultPage={listData.current_page}
                                                                paginationTotalRows={listData.total_count}
                                                                lastPage={listData.total_page}
                                                            />
                                                        </div>
                                                    </div>
                                                    :
                                                    <EmptyScreen
                                                        image={require('../../Assets/Image/NotFound/no-data.png')}
                                                        title='No data found'
                                                    />
                                                }


                                                {/* <h6 className='mt-4 px-3 e-text-chinese-black e-primary-font e-font-16 e-font-weight-600 line-height-24px'>
                                                    External Service Errors
                                                </h6>
                                                <div className='e-border-1px-platinum-light  border-radius-8 pt-1 mx-3'>
                                                    <div className={`e-data-table-wrapper-default-updated`}>
                                                        <CustomDataTable columns={FAILURE_TABLE_COLUMN}
                                                            data={listData.failed_api}
                                                            pagination={false}
                                                            paginationDefaultPage={listData.current_page}
                                                            paginationTotalRows={listData.total_count}
                                                            lastPage={listData.total_page}
                                                        />
                                                    </div>
                                                </div> */}

                                                <h6 className='mt-4 px-3 e-text-chinese-black e-primary-font e-font-16 e-font-weight-600 line-height-24px'>
                                                    API List
                                                </h6>
                                                <div className='e-border-1px-platinum-light border-radius-8 pt-1 mx-3'>
                                                    <div className={`e-data-table-wrapper-striped padding-32px-  `}>
                                                        <CustomDataTable columns={TABLE_COLUMN_API_STATUS}
                                                            data={listData.data}
                                                            pagination={false}
                                                            // paginationPerPage={10}
                                                            expandableRowsComponent={_expandableRows}
                                                            // defaultExpandedRows={(row) => (row.sub_groups !== null ? true : false)}
                                                            defaultExpandedRows={true}
                                                            striped
                                                            paginationDefaultPage={listData.current_page}
                                                            paginationTotalRows={listData.total_count}
                                                            lastPage={listData.total_page}
                                                        // onChangePage={_handleTablePageChange}
                                                        // rowClick={(e) => _handleNavigate(e.order_id)}
                                                        />
                                                    </div>
                                                </div>
                                            </>
                                        }
                                    </>
                                    :
                                    <EmptyScreen
                                        image={require('../../Assets/Image/NotFound/no-data.png')}
                                        title='No data found'
                                    />
                            }

                        </div>
                    </div>
                </div>
            </section>

            {viewApisModal === true &&
                <ViewApisModal
                    data={viewApisData}
                    handleClose={() => {
                        setTimeout(() => {
                            try {
                                sessionStorage.removeItem(OPS_VIEW_APIS_SESSION_KEY);
                            } catch (_) {
                                /* ignore */
                            }
                            setViewApisModal(false);
                            setViewApisData([]);
                            navigate('/ops', {
                                state: {
                                    tab_index: 2,
                                    api_data: [],
                                }
                            });
                        }, 200);
                    }}
                />
            }
        </Fragment>
    )
}

export default OpsDashboard;
